// One-shot reset of every event-scoped NSB singleton. Called when the event (request.id) changes.
//
// Next.js is a SPA: navigating from one event to another does not unmount the line-items page, so
// these module-level stores survive and would otherwise bleed the previous event's state into the
// next one (resolutions are keyed by item name; the method/flavor selection has no event key at all).
//
// Leaf module: imports only the three standalone stores (no renderer / provider imports) → no cycle.
import { resetResolvedItemNameToUse } from './nsbResolvedItemBridge';
import { resetLotOverrides } from './nsbOverrideStore';
import { resetNsbSelection } from './nsbSelectionStore';

// The per-row applied-state stores (auto-applied/seed guards, applied price+flavor, baseline
// equivalents) live next to the renderers and can't be imported here without an import cycle, so
// each renderer registers a resetter for its own stores. They are ALL cleared on every event change
// so a new event re-derives every row from scratch — identical inputs → identical results, kept
// coherent with the resolved-name bridge reset. The by-name flavors cache and the match-set cache
// ALSO register resetters here, so they too are cleared on every event change — otherwise the same
// item name would bleed the previous event's cached flavors/matches into the next event (the
// cross-event bug this PR fixes).
const appliedStateResetters = new Set<() => void>();
export const registerNsbEventResetter = (fn: () => void): void => { appliedStateResetters.add(fn); };

// Per-row line-item state only: resolved matches + every renderer's registered applied-state / cache
// resetter (applied prices, seed guards, exact-match & See-Intel badges, per-row config, and the
// by-name match/flavor caches). Does NOT touch the event-level method selection or lot overrides.
// Used on an event change (via resetNsbEventState) AND on a same-event replace-all upload — where the
// buyer keeps their chosen method but every row is rebuilt and must re-seed from scratch, otherwise
// stale applied prices / seed guards make freshly-matched rows read "Manual Entry".
export const resetNsbLineItemState = (): void => {
  resetResolvedItemNameToUse();
  appliedStateResetters.forEach(fn => fn());
};

// Full reset for an event (request.id) change: the per-row line-item state above PLUS the event-scoped
// method selection and lot overrides, which must not bleed into the next event.
export const resetNsbEventState = (): void => {
  resetNsbSelection();
  resetLotOverrides();
  resetNsbLineItemState();
};
